import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {
  FiUsers, FiGlobe, FiTrendingUp, FiDatabase, FiActivity,
  FiArrowUp, FiArrowDown, FiRefreshCw, FiGrid, FiList,
  FiExternalLink, FiEdit2, FiClock, FiZap, FiTrash2, FiKey, FiX
} from 'react-icons/fi';
import supabase from '../../supabase/supabase';
import TopNav from '../builder/TopNav';

// ── Metric Card ───────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, change, trend, icon: Icon, color, isLoading, large }) => {
  const trendColor = trend === 'up' ? 'text-[#c8f542]' : trend === 'down' ? 'text-red-400' : 'text-gray-500';
  const trendBg   = trend === 'up' ? 'bg-[#c8f542]/10' : trend === 'down' ? 'bg-red-500/10' : 'bg-gray-500/10';
  const TrendIcon = trend === 'up' ? FiArrowUp : trend === 'down' ? FiArrowDown : FiActivity;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className={`bg-[#1e2228] rounded-2xl border border-[#2c313a] p-6 hover:shadow-xl hover:shadow-black/40 transition-all ${large ? 'border-[#c8f542]/30' : ''}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl ${large ? 'bg-[#c8f542]/20' : `${color}/10`}`}>
          <SafeIcon icon={Icon} className={`${large ? 'w-8 h-8' : 'w-6 h-6'} ${large ? 'text-[#c8f542]' : color}`} />
        </div>
        {change !== null && (
          <div className={`flex items-center space-x-1 px-2.5 py-1 rounded-full ${trendBg}`}>
            <SafeIcon icon={TrendIcon} className={`w-3 h-3 ${trendColor}`} />
            <span className={`text-xs font-black ${trendColor}`}>{change}</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-gray-500 text-xs font-black uppercase tracking-widest mb-1">{title}</p>
        {isLoading ? (
          <div className={`${large ? 'h-14 w-32' : 'h-10 w-24'} bg-[#2c313a] rounded-lg animate-pulse`} />
        ) : (
          <p className={`text-white font-black ${large ? 'text-5xl' : 'text-3xl'}`}>{value}</p>
        )}
      </div>
    </motion.div>
  );
};

// ── User Row ──────────────────────────────────────────────────────────────────
const UserRow = ({ user, onViewSites, onResetPassword }) => {
  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  return (
    <tr className="hover:bg-[#1e2228] transition-colors border-b border-[#2c313a]">
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-[#c8f542]/20 flex items-center justify-center text-[#c8f542] font-black text-sm shrink-0">
            {user.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <p className="font-medium text-white">{user.full_name || 'Unnamed User'}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4"><span className="text-white font-bold">{user.website_count || 0}</span></td>
      <td className="px-6 py-4"><span className="text-white font-bold">{user.deployment_count || 0}</span></td>
      <td className="px-6 py-4 text-sm text-gray-500">{fmt(user.created_at)}</td>
      <td className="px-6 py-4 text-sm text-gray-500">{fmt(user.last_sign_in_at)}</td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <button onClick={() => onViewSites(user.id)} title="View Sites" className="p-2 text-gray-400 hover:text-[#c8f542] hover:bg-[#c8f542]/10 rounded-lg transition-all">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
          </button>
          <button onClick={() => onResetPassword(user)} title="Reset Password" className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all">
            <SafeIcon icon={FiKey} className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ── Site Card ─────────────────────────────────────────────────────────────────
const SiteCard = ({ site, onEdit, onDelete }) => {
  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4 }}
      className="bg-[#1e2228] rounded-2xl border border-[#2c313a] overflow-hidden group hover:shadow-xl hover:shadow-black/40 transition-all"
    >
      <div className="aspect-video relative overflow-hidden bg-[#16191f]">
        {site.banner_url ? (
          <img src={site.banner_url} alt={site.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <SafeIcon icon={FiGlobe} className="w-12 h-12 text-[#c8f542]/20" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2 z-20">
          <button onClick={(e) => { e.stopPropagation(); onEdit(site); }} className="p-2.5 bg-[#c8f542] text-black rounded-xl hover:bg-[#d4ff50] transition-all font-bold" title="Edit Site">
            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
          </button>
          {site.netlify_subdomain && (
            <a href={`https://${site.netlify_subdomain}`} target="_blank" rel="noopener noreferrer"
              className="p-2.5 bg-[#c8f542]/20 text-[#c8f542] border border-[#c8f542]/40 rounded-xl hover:bg-[#c8f542]/30 transition-all" title="View Live">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            </a>
          )}
          <button onClick={(e) => { e.stopPropagation(); onDelete(site); }} className="p-2.5 bg-red-500/20 text-red-500 border border-red-500/40 rounded-xl hover:bg-red-500/30 transition-all" title="Delete Site">
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-white truncate mb-2">{site.name}</h3>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">{site.owner_email}</span>
          <span className="text-gray-500">{fmt(site.updated_at)}</span>
        </div>
      </div>
    </motion.div>
  );
};

// ── Main Super Admin Dashboard ────────────────────────────────────────────────
const SuperAdminDashboard = ({ onClose, onOpenWebsite, onOpenBuilder, onOpenStore, onOpenSites, menuStyles, onMenuStylesChange }) => {
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [tabView, setTabView] = useState('overview');
  const [users, setUsers] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  
  // Password Reset State
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      // Try RPC first, fall back to direct queries
      const { data, error } = await supabase.rpc('get_current_analytics');
      if (!error && data) {
        setAnalytics(data);
      } else {
        // Fallback: direct queries
        const [usersRes, websitesRes] = await Promise.allSettled([
          supabase.from('user_profiles').select('id, created_at'),
          supabase.from('websites_1772750000000').select('id, created_at, is_published, user_id'),
        ]);

        const profiles = usersRes.status === 'fulfilled' ? (usersRes.value.data || []) : [];
        const websites = websitesRes.status === 'fulfilled' ? (websitesRes.value.data || []) : [];

        const today = new Date(); today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);

        const newSignupsToday = profiles.filter(p => new Date(p.created_at) >= today).length;
        const newSignupsYesterday = profiles.filter(p => {
          const d = new Date(p.created_at);
          return d >= yesterday && d < today;
        }).length;
        const newWebsitesToday = websites.filter(w => new Date(w.created_at) >= today).length;
        const newWebsitesYesterday = websites.filter(w => {
          const d = new Date(w.created_at);
          return d >= yesterday && d < today;
        }).length;

        // Count deployments per user
        const deploysByUser = {};
        websites.filter(w => w.is_published).forEach(w => {
          deploysByUser[w.user_id] = (deploysByUser[w.user_id] || 0) + 1;
        });
        const totalDeploys = Object.values(deploysByUser).reduce((a, b) => a + b, 0);
        const avgDeploys = profiles.length > 0 ? Math.round((totalDeploys / profiles.length) * 10) / 10 : 0;

        setAnalytics({
          total_users: profiles.length,
          total_websites: websites.length,
          new_signups_today: newSignupsToday,
          new_signups_yesterday: newSignupsYesterday,
          new_websites_today: newWebsitesToday,
          new_websites_yesterday: newWebsitesYesterday,
          avg_deployments_per_user: avgDeploys,
          total_media_mb: 0,
        });
      }
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Analytics error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data: profiles } = await supabase.from('user_profiles').select('*').order('created_at', { ascending: false });
      const { data: websites } = await supabase.from('websites_1772750000000').select('user_id, is_published');

      const websiteCounts = {};
      const deploymentCounts = {};
      (websites || []).forEach(w => {
        websiteCounts[w.user_id] = (websiteCounts[w.user_id] || 0) + 1;
        if (w.is_published) deploymentCounts[w.user_id] = (deploymentCounts[w.user_id] || 0) + 1;
      });

      setUsers((profiles || []).map(p => ({
        ...p,
        website_count: websiteCounts[p.id] || 0,
        deployment_count: deploymentCounts[p.id] || 0,
      })));
    } catch (err) { console.error('Users fetch error:', err); }
  }, []);

  const fetchAllSites = useCallback(async () => {
    try {
      const { data: sites } = await supabase
        .from('websites_1772750000000')
        .select('*')
        .order('updated_at', { ascending: false });

      const { data: profiles } = await supabase.from('user_profiles').select('id, email');
      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.id] = p.email; });

      const enriched = (sites || []).map(s => ({ ...s, owner_email: profileMap[s.user_id] || 'Unknown' }));
      setAllSites(enriched);
      setFilteredSites(enriched);
    } catch (err) { console.error('Sites fetch error:', err); }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  useEffect(() => {
    if (tabView === 'users') fetchUsers();
    if (tabView === 'sites') fetchAllSites();
  }, [tabView, fetchUsers, fetchAllSites]);

  const handleViewUserSites = (userId) => {
    setSelectedUserId(userId);
    setFilteredSites(allSites.filter(s => s.user_id === userId));
    setTabView('sites');
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters long.');
      return;
    }
    setIsResetting(true);
    try {
      const { error } = await supabase.rpc('admin_update_user_password', {
        target_user_id: passwordResetUser.id,
        new_password: newPassword
      });
      if (error) throw error;
      alert(`Password for ${passwordResetUser.email} has been successfully updated.`);
      setPasswordResetUser(null);
      setNewPassword('');
    } catch (err) {
      alert('Failed to reset password: ' + err.message);
    } finally {
      setIsResetting(false);
    }
  };

  const handleDeleteSite = async (site) => {
    if (window.confirm(`Are you sure you want to delete the website "${site.name}"? This action cannot be undone.`)) {
      try {
        const { error } = await supabase.from('websites_1772750000000').delete().eq('id', site.id);
        if (error) throw error;
        setAllSites(prev => prev.filter(s => s.id !== site.id));
        setFilteredSites(prev => prev.filter(s => s.id !== site.id));
      } catch (err) {
        alert('Failed to delete website: ' + err.message);
      }
    }
  };

  const getChange = (cur, prev) => {
    if (!prev || prev === 0) return null;
    const pct = Math.round(((cur - prev) / prev) * 100);
    return `${pct > 0 ? '+' : ''}${pct}%`;
  };
  const getTrend = (cur, prev) => !prev || prev === 0 ? 'neutral' : cur > prev ? 'up' : cur < prev ? 'down' : 'neutral';

  return (
    <div className="flex flex-col h-full w-full bg-[#0a0a0a] font-sans overflow-hidden">
      {/* TopNav with Dashboard tab active + all nav callbacks */}
      <TopNav
        view="dashboard"
        activeWebsiteName="Super Admin"
        onOpenSites={onOpenSites || (() => {})}
        onOpenBuilder={onOpenBuilder || (() => {})}
        onOpenStore={onOpenStore || (() => {})}
        onOpenDashboard={() => {}}
        menuStyles={menuStyles}
        onMenuStylesChange={onMenuStylesChange}
        onUndo={() => {}}
        onRedo={() => {}}
        canUndo={false}
        canRedo={false}
        isPublished={false}
        isPublishing={false}
      />

      {/* Dashboard Header */}
      <div className="bg-[#111318] border-b border-[#2c313a] px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center space-x-2">
            <SafeIcon icon={FiZap} className="w-6 h-6 text-[#c8f542]" />
            <span>Super Admin Dashboard</span>
          </h1>
          <p className="text-xs text-gray-500 flex items-center space-x-2 mt-1">
            <SafeIcon icon={FiClock} className="w-3 h-3" />
            <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
            <span className="text-gray-600">•</span>
            <span>Auto-refreshes every 30s</span>
          </p>
        </div>
        <button
          onClick={fetchAnalytics}
          disabled={isLoading}
          className="p-2.5 bg-[#1e2228] border border-[#2c313a] text-gray-400 hover:text-white rounded-xl transition-all disabled:opacity-40"
        >
          <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Sub Tabs */}
      <div className="bg-[#111318] border-b border-[#2c313a] px-6 py-3 flex items-center space-x-2 shrink-0">
        {[
          { id: 'overview', label: 'Overview', icon: FiActivity },
          { id: 'users', label: 'All Users', icon: FiUsers },
          { id: 'sites', label: 'All Sites', icon: FiGlobe },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setTabView(tab.id)}
            className={`px-4 py-2 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center space-x-2 ${
              tabView === tab.id ? 'bg-[#c8f542] text-black' : 'text-gray-500 hover:text-gray-300 hover:bg-[#1e2228]'
            }`}
          >
            <SafeIcon icon={tab.icon} className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <AnimatePresence mode="wait">

          {/* ── Overview ── */}
          {tabView === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
              {/* Big hero metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <MetricCard title="New Signups Today" value={analytics?.new_signups_today ?? 0}
                  change={getChange(analytics?.new_signups_today, analytics?.new_signups_yesterday)}
                  trend={getTrend(analytics?.new_signups_today, analytics?.new_signups_yesterday)}
                  icon={FiUsers} color="text-[#c8f542]" isLoading={isLoading} large />
                <MetricCard title="New Websites Today" value={analytics?.new_websites_today ?? 0}
                  change={getChange(analytics?.new_websites_today, analytics?.new_websites_yesterday)}
                  trend={getTrend(analytics?.new_websites_today, analytics?.new_websites_yesterday)}
                  icon={FiGlobe} color="text-[#c8f542]" isLoading={isLoading} large />
              </div>

              {/* Secondary metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard title="Total Users" value={analytics?.total_users ?? 0} change={null} trend="neutral" icon={FiUsers} color="text-blue-400" isLoading={isLoading} />
                <MetricCard title="Total Websites" value={analytics?.total_websites ?? 0} change={null} trend="neutral" icon={FiGlobe} color="text-purple-400" isLoading={isLoading} />
                <MetricCard title="Avg Deployments / User" value={analytics?.avg_deployments_per_user ?? 0} change={null} trend="neutral" icon={FiTrendingUp} color="text-green-400" isLoading={isLoading} />
                <MetricCard title="Media Storage (MB)" value={analytics?.total_media_mb ?? 0} change={null} trend="neutral" icon={FiDatabase} color="text-orange-400" isLoading={isLoading} />
              </div>
            </motion.div>
          )}

          {/* ── Users ── */}
          {tabView === 'users' && (
            <motion.div key="users" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="bg-[#111318] rounded-xl border border-[#2c313a] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#1e2228] border-b border-[#2c313a]">
                    <tr>
                      {['User', 'Websites', 'Deployments', 'Joined', 'Last Login', 'Actions'].map(h => (
                        <th key={h} className="text-left text-[10px] font-black text-gray-500 uppercase tracking-wider px-6 py-4">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">Loading users...</td></tr>
                    ) : users.map(u => (
                      <UserRow key={u.id} user={u} onViewSites={handleViewUserSites} onResetPassword={setPasswordResetUser} />
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* ── Sites ── */}
          {tabView === 'sites' && (
            <motion.div key="sites" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="flex items-center justify-between">
                {selectedUserId ? (
                  <button onClick={() => { setSelectedUserId(null); setFilteredSites(allSites); }}
                    className="text-[#c8f542] hover:text-[#d4ff50] font-bold text-sm flex items-center space-x-2">
                    <span>←</span><span>Back to All Sites ({allSites.length})</span>
                  </button>
                ) : <p className="text-gray-500 text-sm">{filteredSites.length} sites total</p>}
                <div className="flex items-center space-x-2 bg-[#1e2228] p-1 rounded-xl border border-[#2c313a]">
                  {[{ id: 'grid', icon: FiGrid }, { id: 'list', icon: FiList }].map(m => (
                    <button key={m.id} onClick={() => setViewMode(m.id)}
                      className={`p-2 rounded-lg transition-all ${viewMode === m.id ? 'bg-[#c8f542] text-black' : 'text-gray-500 hover:text-gray-300'}`}>
                      <SafeIcon icon={m.icon} className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>

              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredSites.map(site => (
                    <SiteCard key={site.id} site={site} onEdit={(s) => onOpenWebsite?.(s.id, s.name)} onDelete={handleDeleteSite} />
                  ))}
                </div>
              ) : (
                <div className="bg-[#111318] rounded-xl border border-[#2c313a] overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#1e2228] border-b border-[#2c313a]">
                      <tr>
                        {['Name', 'Owner', 'Status', 'Updated', 'Actions'].map(h => (
                          <th key={h} className="text-left text-[10px] font-black text-gray-500 uppercase tracking-wider px-6 py-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSites.map(site => (
                        <tr key={site.id} className="hover:bg-[#1e2228] transition-colors border-b border-[#2c313a]">
                          <td className="px-6 py-4 font-medium text-white">{site.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{site.owner_email}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${site.is_published ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                              {site.is_published ? 'Published' : 'Draft'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{new Date(site.updated_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button onClick={() => onOpenWebsite?.(site.id, site.name)}
                                className="p-2 text-gray-400 hover:text-[#c8f542] hover:bg-[#c8f542]/10 rounded-lg transition-all" title="Edit Site">
                                <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeleteSite(site)}
                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all" title="Delete Site">
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Password Reset Modal */}
      <AnimatePresence>
        {passwordResetUser && (
          <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-[#1e2228] border border-[#2c313a] rounded-2xl p-6 w-full max-w-md shadow-2xl relative">
              <button onClick={() => { setPasswordResetUser(null); setNewPassword(''); }} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
              <h3 className="text-xl font-bold text-white mb-2">Reset Password</h3>
              <p className="text-sm text-gray-400 mb-6">Enter a new password for <span className="text-[#c8f542]">{passwordResetUser.email}</span>.</p>
              
              <input
                type="text"
                placeholder="New Password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full bg-[#111318] border border-[#2c313a] text-white rounded-xl px-4 py-3 mb-6 focus:outline-none focus:border-[#c8f542]"
              />
              
              <div className="flex justify-end space-x-3">
                <button onClick={() => { setPasswordResetUser(null); setNewPassword(''); }} className="px-4 py-2 text-gray-400 hover:text-white transition-colors font-bold text-sm">Cancel</button>
                <button onClick={handleResetPasswordSubmit} disabled={isResetting} className="px-6 py-2 bg-[#c8f542] hover:bg-[#d4ff50] text-black font-black rounded-xl transition-colors disabled:opacity-50">
                  {isResetting ? 'Saving...' : 'Reset Password'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SuperAdminDashboard;